import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { isEmpty } from "lodash";
import { catchError, firstValueFrom, Observable, throwError } from "rxjs";
import { APIResponse } from "src/app/models/models";
import { UtilityService } from "src/app/util/utility.service";
import { environment } from "src/environments/environment";
import { CbtaModalComponent } from "../components/cbta-modal/cbta-modal.component";
import { ErrorSnackbarComponent, ErrorSnackbarData } from "../components/cbta-snackbar/error-snackbar.component";
import { shouldSuppressAuthError } from "../utils/auth-session-policy.utils";
import {
	resolveHttpErrorMessage,
	resolveHttpErrorMessageText,
	resolveSignOffHttpErrorPresentation,
	toHttpErrorPayload,
} from "../utils/http-error-message";
import { LoaderService } from "./loader.service";

const baseURL = environment.apiDomain;

@Injectable({
	providedIn: "root",
})
export class ApiHandlerService {
	errorShown: boolean = false;

	constructor(
		private http: HttpClient,
		private loadingService: LoaderService,
		private util: UtilityService,
		private dialog: MatDialog,
		private snackBar: MatSnackBar,
	) {}

	get(url: string, options?: any, params?: any, type?: any) {
		return this.http
			.get(baseURL + url, this.getReqOptions(options, params, type))
			.pipe(catchError((err) => this.catchAuthError(err)));
	}

	getWithSnackbarError(url: string, options?: any, params?: any, type?: any) {
		return this.http
			.get(baseURL + url, this.getReqOptions(options, params, type))
			.pipe(catchError((err) => this.catchErrorWithSnackbar(err)));
	}

	getWithoutSnackbarError(url: string, options?: any, params?: any, type?: any) {
		return this.http.get(baseURL + url, this.getReqOptions(options, params, type));
	}

	getDefault(url: string, options?: any, params?: any, type?: any) {
		return this.http
			.get(baseURL + url, this.getReqOptions(options, params, type))
			.pipe(catchError((err) => console.log("error", err)!));
	}

	getExternal(url: string, options?: any, params?: any, type?: any) {
		return this.http
			.get(url, this.getReqOptions(options, params, type))
			.pipe(catchError((err) => this.catchAuthError(err)));
	}

	getLog(url: string, options?: any, params?: any) {
		return this.http
			.get(baseURL + url, this.getReqOptionsText(options, params))
			.pipe(catchError((err) => this.catchAuthError(err)));
	}

	post(url: string, body: any | null, options?: any, params?: any, reqType?: any) {
		return this.http
			.post(baseURL + url, body, this.getReqOptions(options, params, reqType))
			.pipe(catchError((err) => this.catchAuthError(err)));
	}

	/** POST that preserves the interceptor error payload and does not open an error modal. */
	postQuiet(url: string, body: any | null, options?: any, params?: any, reqType?: any) {
		return this.http.post(baseURL + url, body, this.getReqOptions(options, params, reqType));
	}

	/** PUT that preserves the interceptor error payload and does not open an error modal. */
	putQuiet(url: string, body: any | null, options?: any, params?: any, reqType?: any) {
		return this.http.put(baseURL + url, body, this.getReqOptions(options, params, reqType));
	}

	postWithSnackbarError(url: string, body: any | null, options?: any, params?: any, reqType?: any) {
		return this.http
			.post(baseURL + url, body, this.getReqOptions(options, params, reqType))
			.pipe(catchError((err) => this.catchErrorWithSnackbar(err)));
	}

	postWithSignOffError(url: string, body: any | null, options?: any, params?: any, reqType?: any) {
		return this.http
			.post(baseURL + url, body, this.getReqOptions(options, params, reqType))
			.pipe(catchError((err) => this.catchSignOffError(err)));
	}

	put(url: string, body: any | null, options?: any, params?: any, reqType?: any) {
		return this.http
			.put(baseURL + url, body, this.getReqOptions(options, params, reqType))
			.pipe(catchError((err) => this.catchAuthError(err)));
	}

	patch(url: string, body: any | null, options?: any, params?: any, reqType?: any) {
		return this.http
			.patch(baseURL + url, body, this.getReqOptions(options, params, reqType))
			.pipe(catchError((err) => this.catchAuthError(err)));
	}

	delete(url: string, options?: any, params?: any) {
		return this.http
			.delete(baseURL + url, this.getReqOptions(options, params))
			.pipe(catchError((err) => this.catchAuthError(err)));
	}

	deleteWithoutBody(url: string, options?: any, params?: any) {
		return this.http
			.delete(baseURL + url, this.getReqOptions(options, params))
			.pipe(catchError((err) => this.catchAuthError(err)));
	}

	// apiHandlerService
	deleteWithBody(
		url: string,
		options?: HttpHeaders | { headers?: HttpHeaders },
		extras?: { body?: any; params?: HttpParams | Record<string, any> },
	) {
		const reqOptions: any = {};

		// allow passing headers either directly or wrapped
		if (options instanceof HttpHeaders) reqOptions.headers = options;
		else if (options?.headers) reqOptions.headers = options.headers;

		if (extras?.params) reqOptions.params = extras.params;
		if (extras?.body !== undefined) reqOptions.body = extras.body;

		return this.http.request("DELETE", baseURL + url, reqOptions);
	}

	private getReqOptions(options: any, queryParams?: any, reqType?: any) {
		let tempOptions = options;
		if (!tempOptions) {
			tempOptions = {};
		}

		let headers, parameters;
		if (tempOptions.headers) {
			headers = tempOptions.headers;
		} else {
			headers = new HttpHeaders();
		}

		if (reqType == "csv") {
			headers = headers.append("Content-Type", "text/csv;charset=utf-8;");
			tempOptions.headers = headers;
			tempOptions.responseType = "text";
		} else if (reqType === "pdf") {
			//for multipart-form, let it automatically calculate the body size
			headers = headers.append("Content-Type", "application/pdf");
			tempOptions.headers = headers;
		} else if (reqType === "file") {
			//for multipart-form, let it automatically calculate the body size
			// headers = headers.append('Content-Type', 'multipart/form-data');
			// tempOptions.headers = headers;
		} else {
			headers = headers.append("Content-Type", "application/json");
			tempOptions.headers = headers;
		}

		// query params
		if (!isEmpty(queryParams)) {
			tempOptions.params = queryParams;
		}

		return tempOptions;
	}

	private getReqOptionsText(options: any, queryParams?: any) {
		let tempOptions = options;
		if (!tempOptions) {
			tempOptions = {};
		}

		let headers, parameters;
		if (tempOptions.headers) {
			headers = tempOptions.headers;
		} else {
			headers = new HttpHeaders();
		}

		headers = headers.append("Accept", "text/html, application/xhtml+xml, */*");
		headers = headers.append("Content-Type", "application/x-www-form-urlencoded");
		tempOptions.headers = headers;
		tempOptions.responseType = "text";

		// query params
		if (!isEmpty(queryParams)) {
			tempOptions.params = queryParams;
		}

		return tempOptions;
	}

	private getMaintenanceErrors(error: any): string[] | null {
		if (error?.status !== 503 || !navigator.onLine) return null;
		const errors = error?.error?.errors;
		return Array.isArray(errors) && errors.length > 0
			? errors
			: ["The application is currently under maintenance. Please try again later."];
	}

	private showMaintenanceModal(message: string | string[]): void {
		if (!this.errorShown && navigator.onLine) {
			this.errorShown = true;

			const ref = this.dialog.open(CbtaModalComponent, {
				panelClass: "error-modal",
				height: "220px",
				width: "360px",
				hasBackdrop: true,
				data: {
					modalName: "error-modal",
					modalTitle: "Under Maintenance",
					modalContent: message,
					noAction: true,
					disableClose: true,
				},
			});

			const sub = ref.afterClosed().subscribe((_) => {
				this.errorShown = false;
				sub.unsubscribe();
			});
		}
	}

	private showErrorModal(message: string | string[], title: string = "Error"): void {
		if (!this.errorShown) {
			this.errorShown = true;

			const ref = this.dialog.open(CbtaModalComponent, {
				panelClass: "error-modal",
				height: "220px",
				width: "360px",
				hasBackdrop: true,
				data: {
					modalName: "error-modal",
					modalTitle: title,
					modalContent: message,
					modalBtnConfirmText: "Close",
					noCloseBtn: true,
				},
			});

			const sub = ref.afterClosed().subscribe((_) => {
				this.errorShown = false;
				sub.unsubscribe();
			});
		}
	}

	private catchAuthError(error: any): Observable<Response> {
		const maintenanceErrors = this.getMaintenanceErrors(error);
		if (maintenanceErrors) {
			const errorMessage = maintenanceErrors[0];
			this.showMaintenanceModal(maintenanceErrors);
			this.loadingService.setLoading(false);
			return throwError(() => errorMessage);
		}

		if (this.suppressAuthError(error) || this.suppressOfflineFailure(error)) {
			return throwError(() => error);
		}

		const errorMessage = resolveHttpErrorMessage(error);
		console.error(errorMessage);
		this.showErrorModal(errorMessage);
		this.loadingService.setLoading(false);
		return throwError(() => errorMessage);
	}

	/** Surface a client-side check (e.g. HEIF / oversized photo) with the same error modal as API failures. */
	handleClientError(error: unknown): Observable<never> {
		return this.catchAuthError(error) as Observable<never>;
	}

	private catchErrorWithSnackbar(error: any): Observable<never> {
		const maintenanceErrors = this.getMaintenanceErrors(error);
		if (maintenanceErrors) {
			this.showMaintenanceModal(maintenanceErrors);
			this.loadingService.setLoading(false);
			return throwError(() => error);
		}

		if (this.suppressAuthError(error) || this.suppressOfflineFailure(error)) {
			return throwError(() => error);
		}

		const message = resolveHttpErrorMessageText(error);
		this.snackBar.openFromComponent(ErrorSnackbarComponent, {
			data: {
				message,
			} as ErrorSnackbarData,
			duration: 5000,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["error-snackbar"],
		});
		this.loadingService.setLoading(false);
		return throwError(() => error);
	}

	private catchSignOffError(error: any): Observable<never> {
		const maintenanceErrors = this.getMaintenanceErrors(error);
		if (maintenanceErrors) {
			this.showMaintenanceModal(maintenanceErrors);
			this.loadingService.setLoading(false);
			return throwError(() => maintenanceErrors[0]);
		}

		if (this.suppressAuthError(error)) {
			return throwError(() => error);
		}

		const { title, message } = resolveSignOffHttpErrorPresentation(error);
		console.error("Sign-off error:", message);
		this.showErrorModal(message, title);
		this.loadingService.setLoading(false);
		return throwError(() => message);
	}

	/** Silent when online redirect is in flight, or offline on ratings/pending. */
	private suppressAuthError(error: unknown): boolean {
		if (!shouldSuppressAuthError(error as any)) return false;
		this.loadingService.setLoading(false);
		return true;
	}

	/**
	 * Network drops and gateway timeouts are not application failures.
	 * Status 500 while online still surfaces the server dialog.
	 */
	private suppressOfflineFailure(error: unknown): boolean {
		const status = toHttpErrorPayload(error).status;
		if (!navigator.onLine || status === 0 || status === 502 || status === 504) {
			this.loadingService.setLoading(false);
			return true;
		}
		return false;
	}

	promisifyGet<T = any>(url: string, options?: any, params?: any, type?: any) {
		const resp = this.http.get<APIResponse<T>>(baseURL + url, {
			...this.getReqOptions(options, params, type),
			observe: "events",
		});
		return firstValueFrom(resp);
	}
}
